import { useSelector, useDispatch } from 'react-redux';
import { selectAuth, selectUser, selectIsAuthenticated, selectIsLoading, logout as logoutAction } from '../store/slices/authSlice';
import { apiSlice } from '../store/api/apiSlice';

export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);

  const hasRole = (roleName: string): boolean => {
    return user?.role?.name === roleName;
  };

  const hasAnyRole = (roleNames: string[]): boolean => {
    return user?.role ? roleNames.includes(user.role.name) : false;
  };

  const isAdmin = (): boolean => {
    return hasRole('admin');
  };

  const isManager = (): boolean => {
    return hasRole('manager');
  };

  /** Manager: read-only persona — backend blocks mutations. */
  const isReadOnlyRole = (): boolean => {
    return isManager();
  };

  const isLogist = (): boolean => {
    return hasAnyRole(['admin', 'logist']);
  };

  const isWarehouseWorker = (): boolean => {
    return hasAnyRole(['admin', 'logist', 'warehouse_worker']);
  };

  const isDriver = (): boolean => {
    return hasRole('driver');
  };

  const isClient = (): boolean => {
    return hasRole('client');
  };

  const canViewAllApplications = (): boolean => {
    return hasAnyRole(['admin', 'logist', 'warehouse_worker']);
  };

  const canCreateApplications = (): boolean => {
    return hasAnyRole(['admin', 'logist', 'client']);
  };

  const canEditApplications = (): boolean => {
    return hasAnyRole(['admin', 'logist']);
  };

  const canManageRoutes = (): boolean => {
    return hasAnyRole(['admin', 'logist']);
  };

  const canUpdateRouteStatus = (): boolean => {
    return hasAnyRole(['admin', 'logist', 'driver']);
  };

  const canManageWarehouses = (): boolean => {
    return hasAnyRole(['admin', 'logist', 'warehouse_worker']);
  };

  const canViewReports = (): boolean => {
    return hasAnyRole(['admin', 'logist']);
  };

  const canManageUsers = (): boolean => {
    return hasRole('admin');
  };

  const logout = () => {
    dispatch(logoutAction());
    dispatch(apiSlice.util.resetApiState());
  };

  return {
    ...auth,
    user,
    isAuthenticated,
    isLoading,
    hasRole,
    hasAnyRole,
    isAdmin,
    isManager,
    isReadOnlyRole,
    isLogist,
    isWarehouseWorker,
    isDriver,
    isClient,
    canViewAllApplications,
    canCreateApplications,
    canEditApplications,
    canManageRoutes,
    canUpdateRouteStatus,
    canManageWarehouses,
    canViewReports,
    canManageUsers,
    logout,
  };
}; 